import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContactSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(currentLanguage === 'en' ?'Hello, I would like to schedule a consultation with Peter & Partners Law Firm.' :'Hujambo, ningependa kupanga mazungumzo na Peter & Partners Law Firm.');
    window.open(`https://wa.me/254721320787?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open('tel:+254721320787', '_self');
  };

  const handleEmailClick = () => {
    window.open('mailto:info@peterpartnerslaw.co.ke', '_self');
  };

  const contactMethods = [
    {
      icon: 'Phone',
      title: currentLanguage === 'en' ? 'Call Us' : 'Tupigie Simu',
      description: currentLanguage === 'en' ? 'Speak directly with our legal team' : 'Zungumza moja kwa moja na timu yetu ya kisheria',
      value: '+254 721 320 787',
      action: handleCallClick,
      color: 'primary'
    },
    {
      icon: 'MessageCircle',
      title: currentLanguage === 'en' ? 'WhatsApp' : 'WhatsApp',
      description: currentLanguage === 'en' ? 'Quick consultation via WhatsApp' : 'Mazungumzo ya haraka kupitia WhatsApp',
      value: currentLanguage === 'en' ? 'Chat with us' : 'Zungumza nasi',
      action: handleWhatsAppClick,
      color: 'success'
    },
    {
      icon: 'Mail',
      title: currentLanguage === 'en' ? 'Email Us' : 'Tutumie Barua Pepe',
      description: currentLanguage === 'en' ? 'Send us your legal inquiry' : 'Tutumie swali lako la kisheria',
      value: 'info@peterpartnerslaw.co.ke',
      action: handleEmailClick,
      color: 'accent'
    }
  ];

  const officeHours = [
    {
      day: currentLanguage === 'en' ? 'Monday - Friday' : 'Jumatatu - Ijumaa',
      time: '8:00 AM - 6:00 PM'
    },
    {
      day: currentLanguage === 'en' ? 'Saturday' : 'Jumamosi',
      time: '9:00 AM - 2:00 PM'
    },
    {
      day: currentLanguage === 'en' ? 'Sunday' : 'Jumapili',
      time: currentLanguage === 'en' ? 'Emergency Only' : 'Dharura Tu'
    }
  ];

  const content = {
    en: {
      sectionTitle: "Ready to Get Started?",
      sectionSubtitle: "Contact us today for a consultation",
      mainDescription: "Don\'t let legal challenges overwhelm you. Our experienced team is here to provide the guidance and representation you need. Contact us today to discuss your case and learn how we can help protect your interests.",
      officeHoursTitle: "Office Hours",
      emergencyNote: "Emergency consultations available 24/7",
      scheduleButton: "Schedule Consultation",
      contactPageButton: "Visit Contact Page"
    },
    sw: {
      sectionTitle: "Uko Tayari Kuanza?",
      sectionSubtitle: "Wasiliana nasi leo kwa mazungumzo",
      mainDescription: "Usiache changamoto za kisheria zikushinde. Timu yetu ya wataalamu iko hapa kutoa mwongozo na uwakilishi unaohitaji. Wasiliana nasi leo kujadili kesi yako na kujifunza jinsi tunaweza kusaidia kulinda maslahi yako.",
      officeHoursTitle: "Masaa ya Ofisi",
      emergencyNote: "Mazungumzo ya dharura yanapatikana 24/7",
      scheduleButton: "Panga Mazungumzo",
      contactPageButton: "Tembelea Ukurasa wa Mawasiliano"
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-background to-surface/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full">
              <Icon name="Phone" size={20} className="text-primary" />
              <span className="text-sm font-medium text-primary">
                {currentLanguage === 'en' ? 'Get In Touch' : 'Wasiliana Nasi'}
              </span>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            {currentContent.sectionTitle}
          </h2>
          
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-8">
            {currentContent.sectionSubtitle}
          </p>

          <p className="text-text-secondary max-w-4xl mx-auto leading-relaxed">
            {currentContent.mainDescription}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Contact Methods */}
          <div className="lg:col-span-2">
            <div className="grid md:grid-cols-3 gap-6">
              {contactMethods.map((method, index) => (
                <div key={index} className="card-elevated hover:shadow-lg transition-all duration-300 group cursor-pointer" onClick={method.action}>
                  <div className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 bg-${method.color}/10 rounded-full flex items-center justify-center group-hover:bg-${method.color} transition-all duration-300`}>
                      <Icon 
                        name={method.icon} 
                        size={24} 
                        className={`text-${method.color} group-hover:text-white transition-all duration-300`} 
                      />
                    </div>
                    
                    <h3 className="font-heading font-semibold text-text-primary mb-2">
                      {method.title}
                    </h3>
                    
                    <p className="text-text-secondary text-sm mb-3">
                      {method.description}
                    </p>
                    
                    <p className={`text-${method.color} font-medium text-sm`}>
                      {method.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Office Hours */}
          <div className="card-elevated">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={24} className="text-warning" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary">
                  {currentContent.officeHoursTitle}
                </h3>
              </div>

              <div className="space-y-4">
                {officeHours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-border-light last:border-b-0">
                    <span className="text-text-secondary text-sm">{schedule.day}</span>
                    <span className="text-text-primary font-medium text-sm">{schedule.time}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-error/5 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                  <p className="text-error text-sm">
                    {currentContent.emergencyNote}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={handleWhatsAppClick}
              iconName="Calendar"
              iconPosition="left"
            >
              {currentContent.scheduleButton}
            </Button>

            <Link to="/contact-us">
              <Button
                variant="outline"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
              >
                {currentContent.contactPageButton}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;